/**
 * Fractional metre/kg stepper for cart LINES (cart drawer + any native
 * cart-products.liquid usage). Companion to fabric-quantity.js (product page)
 * and sections/tessuti-cart.liquid's inline .t-cart__qty--fq script (cart page).
 *
 * Loaded once globally (snippets/scripts.liquid), because the drawer is
 * present on every page and its rows get morphed/replaced on every cart
 * update. Direct listeners bound at load time would go stale after the first
 * morph, so everything here is delegated on `document` and looked up via
 * event.target.closest(...) at the moment the event fires.
 *
 * Loaded as a real ES module (type="module") with a static top-level import
 * - matching page-view-event.js and product-form.js - not a dynamic
 * import(). Shopify's asset build rewrites dynamic import('@shopify/events')
 * into a CommonJS `require(...)` call that doesn't exist in the browser;
 * static imports are left as native ESM and resolve through the theme's
 * import map correctly.
 *
 * 0.25 m step pilot: boxes with data-ui-step-units (pilot products only, see
 * snippets/fabric-quarter-step.liquid) step by that many units, snap typed
 * values to the nearest step, target the line by its key, and always
 * re-render from the cart Shopify holds (see applyPilot). Other boxes run the
 * original code path unchanged.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

(function () {
  if (window.__fqCartLineInit) return;
  window.__fqCartLineInit = true;

  function round(value, dp) {
    var m = Math.pow(10, dp);
    return Math.round(value * m) / m;
  }

  function decimalsFor(step) {
    var t = step.toFixed(10).replace(/0+$/, '');
    var i = t.indexOf('.');
    return i < 0 ? 0 : t.length - i - 1;
  }

  function parseQty(v) {
    return parseFloat(String(v == null ? '' : v).replace(',', '.').trim());
  }

  function setBusy(box, busy) {
    box.querySelectorAll('button').forEach(function (b) {
      b.disabled = busy;
    });
  }

  function showError(box, message) {
    var el = box.querySelector('[data-fq-cartline-error]');
    if (!el) return;
    el.textContent = message || '';
    if (message) el.removeAttribute('hidden');
    else el.setAttribute('hidden', '');
  }

  function updatePreview(box) {
    var input = box.querySelector('[data-fq-cartline-input]');
    var note = box.querySelector('[data-fq-cartline-note]');
    if (!input || !note) return;
    var baseCount = parseFloat(box.dataset.baseCount) || 0;
    var baseUnit = box.dataset.baseUnit || '';
    var v = parseQty(input.value);
    if (isNaN(v) || !(baseCount > 0) || !baseUnit) return;
    note.textContent = 'approx. ' + round(v * baseCount, 2) + ' ' + baseUnit;
  }

  // The cart drawer's own subtree is marked data-skip-node-update (see
  // snippets/cart-drawer.liquid) so the section-rendering morph preserves
  // its DOM/focus/animation state instead of replacing it wholesale. That
  // means the static "Quantity: X m" properties line rendered by
  // snippets/cart-products.liquid (a plain <dd>, not a hydrated ref) is
  // NOT guaranteed to be refreshed by the morph after this fetch resolves.
  // Rather than depend on that morph timing/behavior, patch it directly and
  // immediately here so it can never go stale relative to the stepper /
  // price, which are already updated imperatively in this same handler.
  function syncPropertyDisplay(box, quantityText) {
    // Start from the row, not the box: the box itself carries data-key and
    // Element.closest() includes the element itself, so box.closest('[data-key]')
    // returned the box and never reached the row's <dd>.
    var row = box.closest('tr') || (box.parentElement && box.parentElement.closest('[data-key]'));
    if (!row) return;
    var dd = row.querySelector('[data-cart-property="Quantity"]');
    if (dd) dd.textContent = quantityText;
  }

  function sectionIds() {
    return Array.prototype.map
      .call(document.querySelectorAll('cart-items-component'), function (el) {
        return el.dataset.sectionId;
      })
      .filter(Boolean);
  }

  // Liquid's `item.properties | json` can serialize as either a plain
  // object ({"Quantity":"0.5 m"}) or an array of [key, value] pairs
  // depending on how the properties drop was passed through. Normalize
  // both shapes so a stray "0" key never gets written back to the cart.
  function parseProps(raw) {
    var props = {};
    var data;
    try {
      data = JSON.parse(raw || '{}');
    } catch (e) {
      return props;
    }
    if (Array.isArray(data)) {
      data.forEach(function (pair) {
        if (Array.isArray(pair) && pair.length === 2) props[pair[0]] = pair[1];
      });
    } else if (data && typeof data === 'object') {
      props = data;
    }
    return props;
  }

  // --- 0.25 m step pilot ----------------------------------------------------
  // snippets/fabric-quantity-cart-line.liquid adds data-ui-step-units,
  // data-divisor and data-variant-id to pilot lines only (gate:
  // snippets/fabric-quarter-step.liquid, the same one the product page and the
  // cart page use). Every quantity here is an integer count of qty_step units,
  // exactly what Shopify bills; metres are only ever derived from it. Lines
  // without data-ui-step-units never reach this code.

  function uiUnits(box) {
    return parseInt(box.dataset.uiStepUnits, 10) || 0;
  }

  function divisorFor(box) {
    var div = parseInt(box.dataset.divisor, 10);
    return div > 0 ? div : Math.round(1 / (parseFloat(box.dataset.step) || 0.05));
  }

  /** Units -> metres with exactly two decimals, from integer hundredths (25 at 20/m -> "1.25"). */
  function metresText(box, units) {
    var h = Math.round((units * 100) / divisorFor(box));
    var f = h % 100;
    return Math.floor(h / 100) + '.' + (f < 10 ? '0' : '') + f;
  }

  /** The Quantity property text for a unit count ("1.25 m"). */
  function quantityText(box, units) {
    var uom = box.dataset.uom || '';
    return metresText(box, units) + (uom ? ' ' + uom : '');
  }

  function pilotBounds(box) {
    var min = parseInt(box.dataset.minUnits, 10) || uiUnits(box) || 1;
    var max = box.dataset.maxUnits ? parseInt(box.dataset.maxUnits, 10) : null;
    return { min: min, max: max };
  }

  /** -/+ are disabled at the pilot min/max (the server render does the same). */
  function syncPilotButtons(box) {
    var cur = parseInt(box.dataset.currentUnits, 10) || 0;
    var b = pilotBounds(box);
    box.querySelectorAll('[data-fq-cartline-delta]').forEach(function (btn) {
      var down = parseInt(btn.dataset.fqCartlineDelta, 10) < 0;
      btn.disabled = down ? cur <= b.min : b.max != null && cur >= b.max;
    });
  }

  function pilotDone(box) {
    setBusy(box, false);
    delete box.dataset.fqBusy;
    syncPilotButtons(box);
  }

  function errorText(data, fallback) {
    var e = data && (data.errors || data.description || data.message);
    if (e && typeof e === 'object') {
      try {
        e = Object.keys(e)
          .map(function (k) {
            return [].concat(e[k]).join(' ');
          })
          .join(' ');
      } catch (err) {
        e = '';
      }
    }
    return String(e || fallback);
  }

  function isStockError(msg) {
    return /sold out|out of stock|in stock|availab|maximum|can.t add more|cannot add more|can only add|only \d+|were added|in your cart/i.test(
      msg || ''
    );
  }

  function variantUnits(cart, variantId) {
    var total = 0;
    ((cart && cart.items) || []).forEach(function (item) {
      if (String(item.variant_id) === String(variantId)) total += item.quantity;
    });
    return total;
  }

  /**
   * Shopify's stock text counts qty_step units ("Only 140 items were added to
   * your cart."), so say it in metres, from the cart as Shopify now holds it.
   */
  function stockMessage(box, raw, cart, variantId, wantedUnits) {
    var have = variantUnits(cart, variantId);
    if (!(have > 0)) return 'Sorry, this fabric is out of stock.';
    if (/were added|maximum quantity|can.t add more|cannot add more|already in your cart/i.test(raw || '')) {
      return 'Only ' + quantityText(box, have) + ' of this fabric is in stock, and all of it is now in your cart.';
    }
    return (
      'Sorry, there is not enough of this fabric in stock for ' +
      quantityText(box, wantedUnits) +
      '. Your cart has ' +
      quantityText(box, have) +
      ' of it.'
    );
  }

  function postChange(payload) {
    return fetch(Theme.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    }).then(function (res) {
      return res.text().then(function (text) {
        var data;
        try {
          data = JSON.parse(text);
        } catch (e) {
          data = null;
        }
        return { ok: res.ok, data: data };
      });
    });
  }

  function changeOk(result) {
    var d = result && result.data;
    return !!(
      result.ok &&
      d &&
      Array.isArray(d.items) &&
      !d.errors &&
      !(typeof d.status === 'number' && d.status >= 400)
    );
  }

  function fetchCart() {
    return fetch(Theme.routes.cart_url + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (r) {
      if (!r.ok) throw new Error('Could not read the cart.');
      return r.json();
    });
  }

  /** Fresh drawer HTML (Section Rendering API) when a response carried none. */
  function fetchSections() {
    var ids = sectionIds();
    if (!ids.length) return Promise.resolve(undefined);
    return fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')), {
      credentials: 'same-origin',
    })
      .then(function (r) {
        return r.ok ? r.json() : undefined;
      })
      .catch(function () {
        return undefined;
      });
  }

  function hasSections(sections) {
    return !!(sections && typeof sections === 'object' && Object.keys(sections).length);
  }

  /** Tell the drawer (morph) and the header badge about the cart Shopify now holds. */
  function dispatchCart(cart, lineKey, units, sections) {
    var evt = new CartLinesUpdateEvent({
      action: units > 0 ? 'update' : 'remove',
      context: 'cart',
      lines: [{ id: lineKey, quantity: units }],
      promise: Promise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'fabric-quantity-cart-line',
          didError: false,
        },
      }),
    });
    document.dispatchEvent(evt);
    document.dispatchEvent(new CustomEvent('tessuti:cart-change'));
    return evt;
  }

  /**
   * Show `message` on this fabric's box once the drawer has re-rendered.
   * CartItemsComponent morphs inside its own .then on evt.promise, which it
   * registered while the event was dispatched - before this one - so the
   * message lands on the new box instead of being wiped by the morph.
   */
  function showLineMessage(evt, line, variantId, message, hadSections) {
    var paint = function () {
      var same = Array.prototype.filter.call(document.querySelectorAll('[data-fq-cartline]'), function (b) {
        return b.dataset.variantId === String(variantId);
      });
      var target = same.length === 1 ? same[0] : document.querySelector('[data-fq-cartline][data-line="' + line + '"]');
      if (target) showError(target, message);
    };
    var later = function () {
      setTimeout(paint, 0);
    };
    (evt && evt.promise ? evt.promise : Promise.resolve()).then(later, later);
    // Without section HTML the drawer fetches its own; paint again after that.
    if (!hadSections) setTimeout(paint, 1500);
  }

  /**
   * Make every line of this fabric say what Shopify bills: rewrite the
   * Quantity text of any line whose text no longer matches its units (a stock
   * cap keeps the text we sent on fewer units; two lines with the same text
   * merge). One line per request, by its current key, quantity unchanged,
   * reading the next state from each response because keys change.
   * Resolves to { cart, sections, changed }.
   */
  function reconcileVariant(box, cart, variantId, pass, sections) {
    var stale = null;
    ((cart && cart.items) || []).forEach(function (item) {
      if (stale || String(item.variant_id) !== String(variantId)) return;
      var p = item.properties;
      if (!p || typeof p !== 'object' || Array.isArray(p) || p.Quantity == null) return;
      var want = quantityText(box, item.quantity);
      if (String(p.Quantity) === want) return;
      var fixed = {};
      Object.keys(p).forEach(function (k) {
        fixed[k] = p[k];
      });
      fixed.Quantity = want;
      stale = { id: item.key, quantity: item.quantity, properties: fixed };
    });
    if (!stale || pass >= 3) return Promise.resolve({ cart: cart, sections: sections, changed: pass > 0 });
    stale.sections = sectionIds().join(',');
    stale.sections_url = window.location.pathname;
    return postChange(stale)
      .then(function (result) {
        if (!changeOk(result)) {
          console.warn('[fabric-quantity-cart-line] could not relabel a cart line', result && result.data);
          return { cart: cart, sections: sections, changed: pass > 0 };
        }
        return reconcileVariant(box, result.data, variantId, pass + 1, result.data.sections);
      })
      .catch(function (err) {
        console.warn('[fabric-quantity-cart-line] could not relabel a cart line', err);
        return { cart: cart, sections: sections, changed: pass > 0 };
      });
  }

  /**
   * Pilot line change. Sends integer units plus the matching Quantity text,
   * then always renders the cart Shopify actually holds: on a stock cap or
   * any error the cart is read back, stale Quantity text is fixed, and the
   * drawer is re-rendered from it, so the stepper never keeps a quantity the
   * cart did not accept.
   */
  function applyPilot(box, units) {
    var input = box.querySelector('[data-fq-cartline-input]');
    if (!input || box.dataset.fqBusy) return;

    var b = pilotBounds(box);
    var currentUnits = parseInt(box.dataset.currentUnits, 10) || b.min;
    var line = parseInt(box.dataset.line, 10);
    var lineKey = box.dataset.key || '';
    var variantId = box.dataset.variantId || '';

    if (!isFinite(units)) units = currentUnits;
    units = Math.round(units);
    if (units < b.min) units = b.min;
    if (b.max != null && units > b.max) units = b.max;

    if (units === currentUnits) {
      input.value = metresText(box, units);
      updatePreview(box);
      return;
    }

    var wanted = quantityText(box, units);
    var props = parseProps(box.dataset.props);
    props.Quantity = wanted;

    showError(box, '');
    setBusy(box, true);
    box.dataset.fqBusy = '1';
    input.value = metresText(box, units);
    updatePreview(box);

    // The line key names exactly this line (a stale key fails safely instead
    // of changing another line); `line` (1-based) is the fallback.
    var payload = { quantity: units, properties: props, sections: sectionIds().join(','), sections_url: window.location.pathname };
    if (lineKey) payload.id = lineKey;
    else payload.line = line;

    function refresh(cart, sections, message) {
      var get = hasSections(sections) ? Promise.resolve(sections) : fetchSections();
      return get.then(function (secs) {
        pilotDone(box);
        var evt = dispatchCart(cart, lineKey, units, secs);
        if (message) showLineMessage(evt, line, variantId, message, hasSections(secs));
      });
    }

    postChange(payload)
      .then(function (result) {
        var data = result.data;
        if (changeOk(result)) {
          var item = data.items[line - 1];
          var sameLine = !!item && String(item.variant_id) === String(variantId);
          var exact =
            sameLine &&
            item.quantity === units &&
            !!item.properties &&
            String(item.properties.Quantity) === wanted;
          return reconcileVariant(box, data, variantId, 0, data.sections).then(function (rec) {
            if (exact && !rec.changed) {
              // Accepted exactly as asked.
              box.dataset.currentUnits = String(units);
              box.dataset.props = JSON.stringify(props);
              input.value = metresText(box, units);
              updatePreview(box);
              syncPropertyDisplay(box, wanted);
              pilotDone(box);
              dispatchCart(data, lineKey, units, data.sections);
              return;
            }
            // 2xx, but Shopify kept a different amount (stock cap) or merged
            // the line into an identical one: show the real cart.
            var capped = sameLine && item.quantity < units;
            return refresh(rec.cart, rec.sections, capped ? stockMessage(box, '', rec.cart, variantId, units) : '');
          });
        }

        // Error (e.g. 422 stock). Shopify may still have changed the line (it
        // caps at what it can sell), so read the cart back and render that.
        var raw = errorText(data, 'Could not update the cart.');
        return fetchCart().then(function (cart) {
          return reconcileVariant(box, cart, variantId, 0, null).then(function (rec) {
            var message = isStockError(raw) ? stockMessage(box, raw, rec.cart, variantId, units) : raw;
            return refresh(rec.cart, rec.sections, message);
          });
        });
      })
      .catch(function (err) {
        pilotDone(box);
        input.value = metresText(box, currentUnits);
        updatePreview(box);
        showError(box, err && err.message ? err.message : 'Could not update the cart.');
      });
  }

  function apply(box, units) {
    if (uiUnits(box) > 0) {
      applyPilot(box, units);
      return;
    }

    var input = box.querySelector('[data-fq-cartline-input]');
    if (!input) return;

    var step = parseFloat(box.dataset.step) || 0.05;
    var minUnits = parseInt(box.dataset.minUnits, 10) || 1;
    var maxUnits = box.dataset.maxUnits ? parseInt(box.dataset.maxUnits, 10) : null;
    var currentUnits = parseInt(box.dataset.currentUnits, 10) || minUnits;
    var uom = box.dataset.uom || '';
    var line = parseInt(box.dataset.line, 10);
    var lineKey = box.dataset.key || '';
    var decimals = decimalsFor(step);

    if (!isFinite(units)) units = minUnits;
    units = Math.round(units);
    if (units < minUnits) units = minUnits;
    if (maxUnits != null && units > maxUnits) units = maxUnits;

    var fixed = (units * step).toFixed(decimals);

    if (units === currentUnits) {
      input.value = fixed;
      updatePreview(box);
      return;
    }

    var props = parseProps(box.dataset.props);
    props.Quantity = fixed + (uom ? ' ' + uom : '');

    showError(box, '');
    setBusy(box, true);

    var body = JSON.stringify({
      line: line,
      quantity: units,
      properties: props,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    });

    fetch(Theme.routes.cart_change_url + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: body,
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var data;
          try {
            data = JSON.parse(text);
          } catch (e) {
            data = null;
          }
          return { ok: res.ok, data: data };
        });
      })
      .then(function (result) {
        var data = result.data;
        if (!result.ok || !data || data.errors) {
          throw new Error((data && (data.errors || data.description || data.message)) || 'Could not update the cart.');
        }

        box.dataset.currentUnits = String(units);
        box.dataset.props = JSON.stringify(props);
        input.value = fixed;
        updatePreview(box);
        syncPropertyDisplay(box, props.Quantity);
        setBusy(box, false);

        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: units > 0 ? 'update' : 'remove',
            context: 'cart',
            lines: [{ id: lineKey, quantity: units }],
            promise: Promise.resolve({
              cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
              detail: {
                sections: data.sections,
                items: data.items,
                itemCount: data.item_count,
                source: 'fabric-quantity-cart-line',
                didError: false,
              },
            }),
          })
        );
      })
      .catch(function (err) {
        setBusy(box, false);
        input.value = (currentUnits * step).toFixed(decimals);
        updatePreview(box);
        showError(box, err && err.message ? err.message : 'Could not update the cart.');
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-fq-cartline-delta]');
    if (!btn) return;
    var box = btn.closest('[data-fq-cartline]');
    if (!box) return;
    var current = parseInt(box.dataset.currentUnits, 10) || 0;
    var delta = parseInt(btn.dataset.fqCartlineDelta, 10);
    var k = uiUnits(box);
    if (k > 0) {
      // Pilot: move to the next / previous whole UI step, so a legacy
      // off-grid line (0.30 m = 6 units) goes + to 0.50 m and - to 0.25 m.
      apply(box, delta > 0 ? Math.floor(current / k) * k + k : Math.ceil(current / k) * k - k);
      return;
    }
    apply(box, current + delta);
  });

  document.addEventListener('input', function (e) {
    var input = e.target.closest && e.target.closest('[data-fq-cartline-input]');
    if (!input) return;
    var box = input.closest('[data-fq-cartline]');
    if (!box) return;
    updatePreview(box);
  });

  // blur does not bubble, so this listener must be registered for the
  // capture phase to still work via delegation from `document`.
  document.addEventListener(
    'blur',
    function (e) {
      var input = e.target.closest && e.target.closest('[data-fq-cartline-input]');
      if (!input) return;
      var box = input.closest('[data-fq-cartline]');
      if (!box) return;
      var step = parseFloat(box.dataset.step) || 0.05;
      var currentUnits = parseInt(box.dataset.currentUnits, 10) || 1;
      var v = parseQty(input.value);
      if (isNaN(v) || v <= 0) {
        apply(box, currentUnits);
        return;
      }
      var k = uiUnits(box);
      if (k > 0) {
        // Pilot: leaving the field without changing the amount must not touch
        // the cart. Otherwise an off-grid line (0.30 m legacy, 1.80 m after a
        // stock cap) is snapped by a mere focus + blur, and the busy state it
        // sets swallows the -/+ click whose mousedown caused the blur.
        var exact = Math.round(v * divisorFor(box) * 1e6) / 1e6;
        if (exact === currentUnits || input.value.trim() === metresText(box, currentUnits)) {
          input.value = metresText(box, currentUnits);
          updatePreview(box);
          return;
        }
        // A changed value snaps to the NEAREST UI step (1.30 -> 1.25).
        apply(box, Math.round(exact / k) * k);
        return;
      }
      // Always round a typed quantity UP to the next multiple of `step`
      // (2.22 -> 2.25, never 2.20) - never give the customer less than they
      // asked for. The epsilon absorbs float noise on an already-exact multiple.
      apply(box, Math.ceil(v / step - 1e-9));
    },
    true
  );

  document.addEventListener('keydown', function (e) {
    var input = e.target.closest && e.target.closest('[data-fq-cartline-input]');
    if (!input) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      input.blur();
    }
  });

  function checkoutAdjustments() {
    var pageBoxes = document.querySelectorAll('.t-cart__qty--fq');
    var boxes = pageBoxes.length ? pageBoxes : document.querySelectorAll('[data-fq-cartline]');
    var changes = [];
    var seen = new Set();
    boxes.forEach(function (box) {
      var line = parseInt(box.dataset.line, 10);
      if (!(line > 0) || seen.has(line)) return;
      seen.add(line);
      var step = parseFloat(box.dataset.step);
      if (!(step > 0 && step < 1)) return;
      var props = parseProps(box.dataset.props);
      if (!props.Quantity) return;
      var selected = parseFloat(String(props.Quantity).replace(',', '.'));
      if (!(selected > 0)) return;
      var target = Math.round(selected / step);
      var min = parseInt(box.dataset.minUnits, 10) || 1;
      var max = parseInt(box.dataset.maxUnits, 10);
      target = Math.max(min, target);
      if (max > 0) target = Math.min(max, target);
      var current = parseInt(box.dataset.currentUnits, 10);
      var correctText = quantityText(box, target);
      if (current === target && String(props.Quantity) === correctText) return;
      props.Quantity = correctText;
      changes.push({ line: line, quantity: target, properties: props });
    });
    return changes;
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('button[name="checkout"], .cart__checkout-button, .t-cart__checkout');
    if (!button) return;
    var changes = checkoutAdjustments();
    if (!changes.length) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (button.dataset.fqCheckoutBusy) return;
    button.dataset.fqCheckoutBusy = '1';
    button.disabled = true;
    changes.reduce(function (chain, change) {
      return chain.then(function () {
        return fetch(Theme.routes.cart_change_url + '.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(change),
        }).then(function (response) {
          return response.json().then(function (cart) {
            if (!response.ok || !cart.items || !cart.items[change.line - 1] ||
                cart.items[change.line - 1].quantity !== change.quantity) {
              throw new Error('Could not match the selected fabric quantity in your cart.');
            }
          });
        });
      });
    }, Promise.resolve()).then(function () {
      window.location.assign('/checkout');
    }).catch(function (error) {
      button.disabled = false;
      delete button.dataset.fqCheckoutBusy;
      alert(error && error.message ? error.message : 'Please review your cart quantity before checkout.');
      window.location.reload();
    });
  }, true);

})();